/**
 * Sets up a custom navigation prevention system for Blazor applications.
 * This approach uses a custom dialog that shows before the native beforeunload dialog.
 * 
 * @param {Object} options - Configuration options for the navigation prevention
 * @param {string} options.title - Custom title to display to the user
 * @param {string} options.message - Custom message to display to the user
 * @param {string} [options.icon] - Icon to display in the dialog
 * @param {Function} [options.shouldConfirm] - Optional function that determines if confirmation should be shown
 * @param {Function} [options.onConfirm] - Optional callback function called when user confirms navigation
 * @param {Function} [options.onCancel] - Optional callback function called when user cancels navigation
 * @returns {Function} A function to remove all event listeners when no longer needed
 */
const setupCustomBeforeUnload = (options) => {
    const {
        title,
        message,
        icon = "warning",
        shouldConfirm = () => true,
        onConfirm = () => { return true; },
        onCancel = () => { return false; }
    } = options;

    let hasUserConfirmed = false;
    let isDialogShowing = false;

    // Handle browser navigation events (back/forward/refresh)
    const beforeUnloadHandler = (event) => {
        if (!shouldConfirm() || hasUserConfirmed || isDialogShowing) {
            return null;
        }

        // We still need to set returnValue for the browser's native dialog
        // which will show if the user confirms our custom dialog
        event.returnValue = message;
        return message;
    };

    /**
     * Shows custom confirmation dialog
     * @returns {Promise<boolean>} Promise that resolves to true if user confirms, false otherwise
     */
    const showCustomConfirmation = () => {
        if (isDialogShowing) return Promise.resolve(false);

        isDialogShowing = true;

        return new Promise(resolve => {
            // Create custom confirm modal elements
            const modalOverlay = document.createElement("div");
            modalOverlay.style.cssText = "position:fixed;top:0;left:0;right:0;bottom:0;background:#000000;background:rgba(0,0,0,0.8);z-index:9999;display:flex;align-items:center;justify-content:center;";

            const modalContent = document.createElement("div");
            modalContent.style.cssText = "background:white;padding:20px;border-radius:5px;max-width:800px;box-shadow:0 3px 10px #000000;box-shadow:0 3px 10px rgba(0,0,0,0.6);text-align: center;";

            const iconElement = document.createElement("div");
            iconElement.style.cssText = "font-size:30px;margin:0 auto;color: black;";
            iconElement.innerHTML = icon === "warning" ? "<i class=\"bi bi-exclamation-triangle\"></i>" : icon === "error" ? "<i class=\"bi bi-bug\"></i>" : icon === "info" ? "<i class=\"bi bi-info-circle\"></i>" : "<i class=\"bi bi-exclamation-triangle\"></i>";

            const titleElement = document.createElement("h3");
            titleElement.textContent = title;
            titleElement.style.margin = "0 0 10px 0";

            const messageElement = document.createElement("p");
            messageElement.textContent = message;

            const buttonContainer = document.createElement("div");
            buttonContainer.style.cssText = "display:flex;justify-content:center;margin-top:15px;";

            const cancelButton = document.createElement("button");
            cancelButton.textContent = "Annulla";
            cancelButton.style.cssText = "margin-right:10px;padding:8px 16px;background:#f1f1f1;border:none;border-radius:4px;cursor:pointer;";

            const confirmButton = document.createElement("button");
            confirmButton.textContent = "Esci";
            confirmButton.style.cssText = "padding:8px 16px;background:#dc3545;color:white;border:none;border-radius:4px;cursor:pointer;";

            // Assemble the modal
            buttonContainer.appendChild(cancelButton);
            buttonContainer.appendChild(confirmButton);

            modalContent.appendChild(iconElement);
            modalContent.appendChild(titleElement);
            modalContent.appendChild(messageElement);
            modalContent.appendChild(buttonContainer);

            modalOverlay.appendChild(modalContent);

            // Add event handlers
            cancelButton.addEventListener("click", () => {
                document.body.removeChild(modalOverlay);
                isDialogShowing = false;
                onCancel();
                resolve(false);
            });

            confirmButton.addEventListener("click", () => {
                document.body.removeChild(modalOverlay);
                isDialogShowing = false;
                hasUserConfirmed = true;
                onConfirm();
                resolve(true);

                // Trigger navigation after user has confirmed
                setTimeout(() => {
                    window.removeEventListener("beforeunload", beforeUnloadHandler);
                    // Try to navigate to the destination the user was trying to reach
                    if (window.lastAttemptedNavigation) {
                        window.location.href = window.lastAttemptedNavigation;
                    }
                }, 0);
            });

            document.body.appendChild(modalOverlay);

            // Auto-focus the cancel button for keyboard navigation
            cancelButton.focus();
        });
    };

    // Store attempted links for navigation after confirmation
    const captureNavigation = (event) => {
        if (!shouldConfirm() || hasUserConfirmed) {
            return;
        }

        const link = event.currentTarget;
        const href = link.href;

        // Only handle actual links with href
        if (href.startsWith("javascript:") || href.includes("#") || !href.includes("logout")) {
            return;
        }

        // Store the attempted navigation
        window.lastAttemptedNavigation = href;

        // Prevent default navigation
        event.preventDefault();
        event.stopPropagation();
        
        link.addEventListener("click", captureNavigation);

        // Show custom confirmation
        showCustomConfirmation().then(confirmed => {
            if (!confirmed) {
                window.lastAttemptedNavigation = null;
            }
        });
    };

    // Hanle navigation for blazor
    const navigateTo = () => {
        // For Blazor navigation
        if (window.Blazor) {
            const originalNavigateTo = window.Blazor._internal.navigationManager.navigateTo;
            window.Blazor._internal.navigationManager.navigateTo = function (uri, forceLoad) {
                if (!shouldConfirm() || hasUserConfirmed || !uri.includes("logout")) {
                    originalNavigateTo.call(window.Blazor._internal.navigationManager, uri, forceLoad);
                    return;
                }

                window.lastAttemptedNavigation = uri;

                showCustomConfirmation().then(confirmed => {
                    if (confirmed) {
                        hasUserConfirmed = true;
                        originalNavigateTo.call(window.Blazor._internal.navigationManager, uri, forceLoad);
                    } else {
                        window.lastAttemptedNavigation = null;
                    }
                });
            };
        }
    }

    // Attach to window close and browser navigation
    window.addEventListener("beforeunload", beforeUnloadHandler);

    // Attach to all links in the document
    document.querySelectorAll("a[href]").forEach(link => {
        link.addEventListener("click", captureNavigation);
    });

    // Handle dynamically added links
    const linkObserver = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            if (mutation.type === "childList") {
                mutation.addedNodes.forEach(node => {
                    if (node.nodeType === 1) { // Element node
                        // Check the added element itself if it's a link
                        if (node.tagName === "A" && node.hasAttribute("href")) {
                            node.addEventListener("click", captureNavigation);
                        }

                        // Check children of the added element for links
                        node.querySelectorAll("a[href]").forEach(link => {
                            link.addEventListener("click", captureNavigation);
                        });
                    }
                });
            }
        });
    });

    // Start observing the document for added links
    linkObserver.observe(document.body, { childList: true, subtree: true });

    navigateTo();

    // Return cleanup function
    return () => {
        window.removeEventListener("beforeunload", beforeUnloadHandler);
        document.querySelectorAll("a[href]").forEach(link => {
            link.removeEventListener("click", captureNavigation);
        });
        linkObserver.disconnect();
    };
};

export default setupCustomBeforeUnload;
